import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchPaymentDetails } from "../../../../features/PaymentHistory/paymentHistoryThunks";
import "./PaymentHistory.css";

const PaymentDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { selectedPayment, loadingDetails } = useSelector(
    (state) => state.paymentHistory
  );

  useEffect(() => {
    if (orderId) {
      dispatch(fetchPaymentDetails(orderId));
    }
  }, [dispatch, orderId]);

  /* ---------------- LOADING & EMPTY ---------------- */
  if (loadingDetails) {
    return <div style={{ padding: 20 }}>Loading payment details...</div>;
  }

  if (!selectedPayment) {
    return <div style={{ padding: 20 }}>No payment details found</div>;
  }

  const p = selectedPayment;
  const invoiceUrl = p.invoicePdfPath
    ? `${process.env.REACT_APP_API_BASE_URL}${p.invoicePdfPath}`
    : null;

  return (
    <div className="pd-container">
      {/* ================= HEADER ================= */}
      <div className="pd-header">
        <button className="pd-back" onClick={() => navigate(-1)}>
          ←
        </button>
        <h3>Payment Detail</h3>
      </div>

      {/* ================= TOP SUMMARY ================= */}
      <div className="pd-top-card">
        <h4>{p.paymentFor?.toUpperCase()}</h4>
        <p>Order ID: {p.orderId}</p>

        <div className="pd-badges">
          <span className={`pd-badge ${p.payment_status}`}>
            {p.payment_status}
          </span>
          <span className="pd-badge">{p.payment_method}</span>
          <span className="pd-badge amount">₹{p.amount}</span>
        </div>
      </div>

      {/* ================= PAYMENT SUMMARY ================= */}
      <div className="pd-card">
        <h5>Payment Summary</h5>

        <div className="pd-row">
          <span>Base Amount</span>
          <strong>INR {p.amount}</strong>
        </div>

        <div className="pd-row">
          <span>GST (18%)</span>
          <strong>INR {p.gstAmount}</strong>
        </div>

        <div className="pd-row total">
          <span>Total Booking Amount</span>
          <strong>INR {p.totalAmount}</strong>
        </div>

        <div className="pd-row">
          <span>Paid Amount</span>
          <strong>INR {p.paidAmount}</strong>
        </div>

        {/* Partial payment details — CAM only */}
        {p.paymentFor === "booklet" && p.isPartialPayment && (
          <>
            <div className="pd-row" style={{ color: "#dc3545" }}>
              <span>Pending Balance</span>
              <strong style={{ color: "#dc3545" }}>INR {p.pendingAmount?.toFixed(2) ?? 0}</strong>
            </div>
            <div
              style={{
                background: "#fff3cd",
                border: "1px solid #ffc107",
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 8,
                fontSize: 13,
              }}
            >
              This is a <strong>partial payment</strong>. The remaining balance of{" "}
              <strong>₹{p.pendingAmount?.toFixed(2) ?? 0}</strong> is due. Go to{" "}
              <strong>My CAM Bookings</strong> to pay the outstanding amount.
            </div>
          </>
        )}

        <div className="pd-row">
          <span>Payment Status</span>
          <strong>{p.payment_status}</strong>
        </div>

        {p.payment_date && (
          <div className="pd-row">
            <span>Payment Date</span>
            <strong>{new Date(p.payment_date).toLocaleString()}</strong>
          </div>
        )}
      </div>

      {/* ================= USER INFO ================= */}
      {p.userId && (
        <div className="pd-card">
          <h5>User Info</h5>

          <div className="pd-row">
            <span>Name</span>
            <strong>{p.userId.fullName}</strong>
          </div>

          <div className="pd-row">
            <span>Mobile</span>
            <strong>{p.userId.mobileNumber}</strong>
          </div>

          <div className="pd-row">
            <span>Email</span>
            <strong>{p.userId.email}</strong>
          </div>
        </div>
      )}

      {/* ================= ORDER DETAILS ================= */}
      <div className="pd-card">
        <h5>Order Details</h5>

        <div className="pd-row">
          <span>Order ID</span>
          <strong>{p.orderId}</strong>
        </div>

        {p.bookingId && (
          <div className="pd-row">
            <span>Booking ID</span>
            <strong>{p.bookingId}</strong>
          </div>
        )}

        <div className="pd-row">
          <span>Payment For</span>
          <strong>{p.paymentFor}</strong>
        </div>

        <div className="pd-row">
          <span>Payment Method</span>
          <strong>{p.payment_method}</strong>
        </div>
      </div>

      {/* ================= INVOICE ================= */}
      {invoiceUrl && (
        <div className="pd-card invoice-card">
          <h5>Invoice</h5>

          <a
            href={invoiceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="pd-invoice-btn"
          >
            View Invoice PDF
          </a>
        </div>
      )}
    </div>
  );
};

export default PaymentDetails;
